import is from '@sindresorhus/is';
import { z } from 'zod';

import { invitationDeliveryStatusSchema } from './position-management';

/**
 * The AI transformation strategy of one Position Architecture, served under
 * `/ai-transformation/architectures/{architectureId}`. Every list, count and matrix point is
 * filtered, paged and aggregated by the backend, so the page stays usable for architectures with tens of thousands of
 * positions.
 *
 * Shared filter query params (all optional and repeatable; OR within one param, AND across params):
 * `jobFamilyId`, `jobClusterId`, `attributeValueId`, `roleId`, `levelId`, `scoreLevel` and `q` (title search). An
 * `attributeValueId` is an option of one of the filters' `attributes` and matches its subtree; options of the same
 * attribute are alternatives, those of different attributes must all match, and an id of no listed attribute is a 400.
 * For tasks, the position filters match a task when at least one position in scope holding it matches.
 *
 * A pick (`pickGrouping` with `pickScoreMin` and `pickScoreMax`, each 0..1, and either `pickHoursMin` and
 * `pickHoursMax` in weekly hours or `pickImpactMin` and `pickImpactMax`, each 0..1) narrows the summary, items and
 * status updates to what a rectangle on the matrix at that grouping holds. Both y-axis pairs at once are a 400. The
 * matrix itself is not narrowed by it; it only marks its points as `picked`.
 *
 * The backend decides everything the page shows: the axes, the option labels, the counts and the statuses an item may
 * be moved to. The client renders what it gets.
 */

const opaqueIdSchema = z.string().trim().min(1);

export const AI_TRANSFORMATION_SUBJECTS = ['POSITION', 'TASK'] as const;
export const aiTransformationSubjectSchema = z.enum(AI_TRANSFORMATION_SUBJECTS);
export type AiTransformationSubject = z.infer<typeof aiTransformationSubjectSchema>;

/** The board's columns, in order. A missing status record reads as `IDLE`. */
export const AI_TRANSFORMATION_STATUSES = ['IDLE', 'PRIORITIZED', 'DEFINED', 'STARTING', 'SCALING', 'FULL'] as const;
export const aiTransformationStatusSchema = z.enum(AI_TRANSFORMATION_STATUSES);
export type AiTransformationStatus = z.infer<typeof aiTransformationStatusSchema>;

/**
 * The automation potential scale. The backend owns the values, their order and the score edges; the client only
 * reads the ordered list from the filters response and never buckets a score itself.
 */
export const AUTOMATION_SCORE_LEVELS = ['LOW', 'MEDIUM', 'HIGH', 'VERY_HIGH'] as const;
export const automationScoreLevelSchema = z.enum(AUTOMATION_SCORE_LEVELS);
export type AutomationScoreLevel = z.infer<typeof automationScoreLevelSchema>;

export const AI_TRANSFORMATION_MATRIX_GROUPINGS = ['ROLE', 'LEVEL', 'POSITION', 'TASK'] as const;
export const aiTransformationMatrixGroupingSchema = z.enum(AI_TRANSFORMATION_MATRIX_GROUPINGS);
export type AiTransformationMatrixGrouping = z.infer<typeof aiTransformationMatrixGroupingSchema>;

export const AI_TRANSFORMATION_ITEM_SORTS = ['SCORE', 'TITLE', 'STATUS_CHANGED'] as const;
export const aiTransformationItemSortSchema = z.enum(AI_TRANSFORMATION_ITEM_SORTS);
export type AiTransformationItemSort = z.infer<typeof aiTransformationItemSortSchema>;

const namedRefSchema = z.object({ id: opaqueIdSchema, title: z.string() });
export type AiTransformationNamedRef = z.infer<typeof namedRefSchema>;

/** A position's answer to one of the filters' attributes; an attribute can have several. */
const aiTransformationAttributeAnswerSchema = namedRefSchema.extend({ attributeId: opaqueIdSchema });
export type AiTransformationAttributeAnswer = z.infer<typeof aiTransformationAttributeAnswerSchema>;

/** Enum values a client does not know yet are dropped instead of failing the whole response; non-strings still fail. */
const knownValues = <T extends z.ZodEnum>(schema: T) =>
    z
        .string()
        .array()
        .transform((values) =>
            values.flatMap((value) => {
                const parsed = schema.safeParse(value);
                return parsed.success ? [parsed.data] : [];
            }),
        );

const hasUnknownValue = (entry: unknown, key: string, schema: z.ZodEnum): boolean =>
    is.plainObject(entry) && is.string(entry[key]) && !schema.safeParse(entry[key]).success;

/**
 * Entries whose `key` holds a value the client does not know yet are dropped; every other entry is validated
 * strictly, so any other contract drift fails the response instead of silently shrinking it.
 */
const entriesOfKnown =
    (key: string, schema: z.ZodEnum) =>
    <T extends z.ZodType>(entry: T) =>
        z.preprocess(
            (entries) =>
                is.array(entries) ? entries.filter((value) => !hasUnknownValue(value, key, schema)) : entries,
            entry.array(),
        );

const entriesOfKnownStatus = entriesOfKnown('status', aiTransformationStatusSchema);

const entriesOfKnownScoreLevel = entriesOfKnown('level', automationScoreLevelSchema);

/** Only usable options are listed, sorted by `label`, the text to show (it names the parent when titles repeat). */
export const aiTransformationFilterOptionSchema = z.object({
    id: opaqueIdSchema,
    label: z.string(),
    title: z.string(),
    parentId: opaqueIdSchema.nullish(),
    positionCount: z.number().int().nonnegative(),
});
export type AiTransformationFilterOption = z.infer<typeof aiTransformationFilterOptionSchema>;

/**
 * A Position Management attribute the tenant defines, such as an org unit or a country, with the values positions
 * answer it with as options; an option's `parentId` is its parent value. `label` is already localized.
 */
export const aiTransformationAttributeFilterSchema = z.object({
    id: opaqueIdSchema,
    key: z.string(),
    label: z.string(),
    options: aiTransformationFilterOptionSchema.array(),
    /** Whether its values form an organization tree the POSITION view can group by. */
    treeNode: z.boolean(),
});
export type AiTransformationAttributeFilter = z.infer<typeof aiTransformationAttributeFilterSchema>;

/** `GET .../filters`: the options and scales, independent of what is filtered. */
export const aiTransformationFiltersResponseSchema = z.object({
    /** Sorted by key. */
    attributes: aiTransformationAttributeFilterSchema.array(),
    jobClusters: aiTransformationFilterOptionSchema.array(),
    jobFamilies: aiTransformationFilterOptionSchema.array(),
    /** A level's `parentId` is its role. */
    jobLevels: aiTransformationFilterOptionSchema.array(),
    /** A role's `parentId` is its cluster. */
    jobRoles: aiTransformationFilterOptionSchema.array(),
    /** The statuses a user may move an item to by hand; the others follow from adoption. */
    manualStatuses: knownValues(aiTransformationStatusSchema),
    scoreLevels: knownValues(automationScoreLevelSchema),
    statuses: knownValues(aiTransformationStatusSchema),
});
export type AiTransformationFiltersResponse = z.infer<typeof aiTransformationFiltersResponseSchema>;

export const aiTransformationStatusCountSchema = z.object({
    count: z.number().int().nonnegative(),
    status: aiTransformationStatusSchema,
});
export type AiTransformationStatusCount = z.infer<typeof aiTransformationStatusCountSchema>;

export const aiTransformationScoreLevelCountSchema = z.object({
    count: z.number().int().nonnegative(),
    level: automationScoreLevelSchema,
});
export type AiTransformationScoreLevelCount = z.infer<typeof aiTransformationScoreLevelCountSchema>;

/** `GET .../summary?subject=&<filters>`: every status is listed, zeros included. */
export const aiTransformationSummaryResponseSchema = z.object({
    assessed: z.number().int().nonnegative(),
    statusCounts: entriesOfKnownStatus(aiTransformationStatusCountSchema),
    subject: aiTransformationSubjectSchema,
    total: z.number().int().nonnegative(),
});
export type AiTransformationSummaryResponse = z.infer<typeof aiTransformationSummaryResponseSchema>;

/** A position's employee, as Position Management has them; a name-only employee has no `userId`. */
const aiTransformationAssigneeSchema = z.object({
    email: z.string().nullish(),
    firstName: z.string(),
    /** Set while the employee's invitation waits, is being sent, or was sent or failed. */
    invitationDeliveryStatus: invitationDeliveryStatusSchema.nullish().catch(null),
    lastName: z.string(),
    userId: opaqueIdSchema.nullish(),
});
export type AiTransformationAssignee = z.infer<typeof aiTransformationAssigneeSchema>;

/** A position another one reports to; its `id` is hidden when the caller may not read it. */
const aiTransformationManagerSchema = z.object({
    holderName: z.string().nullish(),
    id: opaqueIdSchema.nullish(),
    title: z.string(),
});
export type AiTransformationManager = z.infer<typeof aiTransformationManagerSchema>;

/**
 * One position or work task. Position-only fields are null or empty for tasks, task-only fields for positions. `area`
 * is the root of the position's organization path in the Position Architecture; it never comes from the Job
 * Architecture.
 */
export const aiTransformationItemSchema = z.object({
    area: namedRefSchema.nullish(),
    /** A task's: the areas of the positions in scope holding it, in title order. */
    areas: namedRefSchema.array().default([]),
    assignee: aiTransformationAssigneeSchema.nullish(),
    /** A position's answers to the filters' attributes, by attribute key, then title; empty for tasks. */
    attributes: aiTransformationAttributeAnswerSchema.array().default([]),
    /** Whether the caller may change the position's employee now. */
    canAssign: z.boolean().default(false),
    /** A task's: how many people hold the positions in scope holding it. */
    holderCount: z.number().int().nonnegative().nullish(),
    holderName: z.string().nullish(),
    /** A task's: the first three of those people, in name order. */
    holderNames: z.string().array().default([]),
    id: opaqueIdSchema,
    /**
     * Business impact, 0..1: a task's own rating, a position's weighed by its tasks' weekly hours; null until every
     * assessed task is rated, and missing from older backends.
     */
    impact: z.number().nullish(),
    jobCluster: namedRefSchema.nullish(),
    jobFamily: namedRefSchema.nullish(),
    level: namedRefSchema.nullish(),
    /** The positions this one reports to, in title order. */
    managers: aiTransformationManagerSchema.array().default([]),
    /** The statuses this caller may set for this item now, in column order; empty when it cannot be moved. */
    movableTo: knownValues(aiTransformationStatusSchema),
    positionCount: z.number().int().nonnegative().nullish(),
    role: namedRefSchema.nullish(),
    score: z.number().nullish(),
    scoreLevel: automationScoreLevelSchema.nullish().catch(null),
    status: aiTransformationStatusSchema,
    statusChangedAt: z.string().nullish(),
    subject: aiTransformationSubjectSchema,
    taskVersion: z.number().int().positive().nullish(),
    title: z.string(),
});
export type AiTransformationItem = z.infer<typeof aiTransformationItemSchema>;

/**
 * `GET .../items?subject=&status=&sort=&page=&size=&<filters>`: one page, `size` at most 200. An
 * item in a status the client does not know yet is left out; any other invalid item fails the page.
 */
export const aiTransformationItemsResponseSchema = z.object({
    items: entriesOfKnownStatus(aiTransformationItemSchema),
    page: z.number().int().nonnegative(),
    size: z.number().int().positive(),
    total: z.number().int().nonnegative(),
});
export type AiTransformationItemsResponse = z.infer<typeof aiTransformationItemsResponseSchema>;

/**
 * The groupings the list nests positions by: the Job Architecture for roles and levels, for positions the value path of
 * the tree attribute (`treeAttributeId`, by default the first attribute with `treeNode`).
 */
export const AI_TRANSFORMATION_TREE_VIEWS = ['ROLE', 'LEVEL', 'POSITION'] as const;
export type AiTransformationTreeView = (typeof AI_TRANSFORMATION_TREE_VIEWS)[number];

export const AI_TRANSFORMATION_TREE_KINDS = ['FAMILY', 'CLUSTER', 'ROLE', 'LEVEL', 'ATTRIBUTE'] as const;
export const aiTransformationTreeKindSchema = z.enum(AI_TRANSFORMATION_TREE_KINDS);
export type AiTransformationTreeKind = z.infer<typeof aiTransformationTreeKindSchema>;

/**
 * One group of the list's tree. `key` is opaque: the client passes it back as the items endpoint's `node` to list the
 * positions sitting directly in the group. A node without `id` and `title` holds the positions the hierarchy leaves
 * out, such as those mapped to no job or without an answer to the tree attribute.
 */
export const aiTransformationTreeNodeSchema = z.object({
    /** Positions sitting directly in this node, listed after its child nodes. */
    directCount: z.number().int().nonnegative(),
    id: opaqueIdSchema.nullish(),
    key: opaqueIdSchema,
    kind: aiTransformationTreeKindSchema,
    parentKey: opaqueIdSchema.nullish(),
    /** Positions in the node's whole subtree. */
    positionCount: z.number().int().nonnegative(),
    /** The subtree's statuses: only those above zero, in column order. */
    statusCounts: entriesOfKnownStatus(aiTransformationStatusCountSchema),
    title: z.string().nullish(),
});
export type AiTransformationTreeNode = z.infer<typeof aiTransformationTreeNodeSchema>;

/**
 * `GET .../tree?view=&<filters>`: every group holding a matching position, parents before their
 * children and siblings in title order.
 */
export const aiTransformationTreeResponseSchema = z.object({
    nodes: aiTransformationTreeNodeSchema.array(),
    view: z.enum(AI_TRANSFORMATION_TREE_VIEWS),
});
export type AiTransformationTreeResponse = z.infer<typeof aiTransformationTreeResponseSchema>;

/**
 * `GET .../locate?view=&id=&<filters>`: where an item sits in the list. For a position, the tree's keys
 * from the root down to the node it sits directly in, and its rank among that node's positions by title; for a task,
 * no keys and its rank in the flat list. An item the filters leave out has no rank.
 */
export const aiTransformationLocationSchema = z.object({
    index: z.number().int().nonnegative().nullable(),
    nodeKeys: opaqueIdSchema.array(),
});
export type AiTransformationLocation = z.infer<typeof aiTransformationLocationSchema>;

export const aiTransformationMatrixPointSchema = z.object({
    /** Positions a role or level point stands for; 1 for a position or task. */
    count: z.number().int().positive(),
    /**
     * Weekly hours the point stands for: a task's own, the sum over a position's tasks, the sum over a role's or level's
     * positions. Null when none of its tasks has an estimate; missing from older backends.
     */
    hours: z.number().nullish(),
    id: opaqueIdSchema,
    /**
     * Business impact, 0..1: a task's own rating, a position's weighed by its tasks' weekly hours, a role's or level's
     * the average of its positions. Null unless everything behind the point is rated; missing from older backends.
     */
    impact: z.number().nullish(),
    peopleShare: z.number(),
    /** Whether the point lies inside the requested pick. */
    picked: z.boolean().default(false),
    score: z.number(),
    /**
     * The score levels of the positions (tasks for `TASK`) the point stands for: only those above zero, low to high,
     * adding up to `count`. A position or task point carries its own level alone. Missing from older backends.
     */
    scoreLevelCounts: entriesOfKnownScoreLevel(aiTransformationScoreLevelCountSchema).optional(),
    /**
     * The statuses of the positions (tasks for `TASK`) the point stands for: only those above zero, in column order,
     * adding up to `count`. A position or task point carries its own status alone.
     */
    statusCounts: entriesOfKnownStatus(aiTransformationStatusCountSchema),
    title: z.string(),
});
export type AiTransformationMatrixPoint = z.infer<typeof aiTransformationMatrixPointSchema>;

const matrixAxisSchema = z.object({
    max: z.number(),
    min: z.number(),
    /** Where the axis splits into its two halves. */
    split: z.number(),
});
export type AiTransformationMatrixAxis = z.infer<typeof matrixAxisSchema>;

/** `GET .../matrix?grouping=&<filters>`: assessed points only, capped at `truncatedAt`. */
export const aiTransformationMatrixResponseSchema = z.object({
    axes: z.object({
        /** Fixed from 0 to 1, split at 0.5; none while no point is rated: null, or missing from older backends. */
        impact: z.preprocess((axis) => axis ?? undefined, matrixAxisSchema.optional()),
        peopleShare: matrixAxisSchema,
        score: matrixAxisSchema,
    }),
    grouping: aiTransformationMatrixGroupingSchema,
    points: aiTransformationMatrixPointSchema.array(),
    truncatedAt: z.number().int().nullish(),
    unassessedCount: z.number().int().nonnegative(),
});
export type AiTransformationMatrixResponse = z.infer<typeof aiTransformationMatrixResponseSchema>;

/**
 * `PUT .../statuses?<filters>`: moves up to 500 items of one subject to a manual status. Items
 * currently in an automatic status are skipped and not counted in `updated`. `summary` is the summary of the request's
 * subject under the same filters after the update.
 */
export type AiTransformationStatusUpdateRequest = {
    readonly ids: readonly string[];
    readonly status: AiTransformationStatus;
    readonly subject: AiTransformationSubject;
};

export const aiTransformationStatusUpdateResponseSchema = z.object({
    summary: aiTransformationSummaryResponseSchema,
    updated: z.number().int().nonnegative(),
});
export type AiTransformationStatusUpdateResponse = z.infer<typeof aiTransformationStatusUpdateResponseSchema>;
